import { useRef } from 'react'
import { Printer, Download } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'

interface CertificateViewerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  html: string | null
  title?: string
}

export function CertificateViewerDialog({
  open,
  onOpenChange,
  html,
  title = 'Warranty Certificate',
}: CertificateViewerDialogProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null)

  function handlePrint() {
    const iframe = iframeRef.current
    if (!iframe?.contentWindow) return
    iframe.contentWindow.print()
  }

  function handleDownload() {
    if (!html) return
    const blob = new Blob([html], { type: 'text/html' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'warranty-certificate.html'
    a.click()
    URL.revokeObjectURL(url)
  }

  const wrappedHtml = html
    ? `<!DOCTYPE html><html><head><style>body{margin:0;padding:32px 40px;background:#fff}</style></head><body>${html}</body></html>`
    : null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="min-w-[720px] max-w-[780px] h-[85vh]"
        bare
      >
        <DialogHeader className="flex-shrink-0 flex flex-row items-center justify-between px-6 py-4 border-b-[0.5px] border-b-[var(--rule)]">
          <DialogTitle className="text-lg">{title}</DialogTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={handleDownload}
              title="Download"
              className="text-[var(--ink-3)] hover:text-[var(--ink)]"
            >
              <Download className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrint}
              className="gap-1.5"
            >
              <Printer className="h-3.5 w-3.5" />
              Print
            </Button>
          </div>
        </DialogHeader>
        <div className="flex-1 min-h-0 overflow-auto bg-[var(--bg-subtle)] p-4">
          {wrappedHtml ? (
            <div className="mx-auto bg-white rounded-[4px] shadow-[0_1px_4px_rgba(0,0,0,0.06)] overflow-hidden">
              <iframe
                ref={iframeRef}
                srcDoc={wrappedHtml}
                title={title}
                className="w-full border-0"
                style={{ minHeight: '1000px' }}
                sandbox="allow-same-origin allow-modals"
              />
            </div>
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-[var(--ink-3)]">
              Loading certificate...
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
